import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import { 
    ShoppingBag, Calendar, Trash2, ChevronRight, 
    ArrowLeft, CheckCircle, Package, Clock, Sparkles
} from 'lucide-react';
import CheckAvailabilityModal from '../components/CheckAvailabilityModal';
import './MyCart.css';

interface CartItem {
    id: string;
    realId?: string;
    name: string;
    price: number;
    image: string;
    vendorName: string;
    vendorType: 'product' | 'service' | 'rental';
    category: string;
    status?: string;
    rentalDetails?: {
        pricePerDay: number;
        securityDeposit: number;
        availableSizes?: string[];
        gender?: 'bridal' | 'groom' | 'unisex';
        rentalCategory?: 'dress' | 'jewelry';
        itemCondition?: string;
    };
    rentalSelection?: {
        startDate: string;
        endDate: string;
        size: string;
    };
}

const MyCart: React.FC = () => {
    const { user, accessToken, loading: authLoading } = useAuth();
    const history = useHistory();
    const [items, setItems] = useState<CartItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [converting, setConverting] = useState(false);
    const [isAvailModalOpen, setIsAvailModalOpen] = useState(false);
    const [selectedVendors, setSelectedVendors] = useState<{id: string, name: string}[]>([]);
    const [confirmedBookings, setConfirmedBookings] = useState<Record<string, boolean>>({});

    useEffect(() => {
        // ...
    }, [accessToken, authLoading, history]);

    const handleToggleConfirmation = async (id: string) => {
        const currentStatus = confirmedBookings[id];
        const newStatus = !currentStatus;
        const backendStatus = newStatus ? 'hired' : 'shortlisted';

        // Update local state first for responsiveness
        setConfirmedBookings(prev => ({
            ...prev,
            [id]: newStatus
        }));

        // Persist to backend if logged in
        if (accessToken) {
            try {
                await fetch(`${API_BASE_URL}/api/market-vendors/shortlist/${id}`, {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ status: backendStatus })
                });
            } catch (err) {
                console.error('Failed to update status on server', err);
                // Optionally revert local state on failure
            }
        }
    };

    const handleSingleEnquiry = (id: string, name: string) => {
        setSelectedVendors([{ id, name }]);
        setIsAvailModalOpen(true);
    };

    const fetchCartItems = async () => {
        setLoading(true);
        setError('');
        try {
            let allItems: CartItem[] = [];
            const localSaved = localStorage.getItem('shadigram_shortlist');
            const localIds: string[] = localSaved ? JSON.parse(localSaved) : [];

            // 1. Fetch from Backend if logged in
            if (accessToken) {
                try {
                    const res = await fetch(`${API_BASE_URL}/api/market-vendors/shortlist`, {
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    });
                    const data = await res.json();
                    if (res.ok) {
                        const backendMapped = data.shortlists
                            .filter((s: any) => s.marketVendorId)
                            .map((s: any) => {
                                const vendor = s.marketVendorId;
                                const rentalSaved = localStorage.getItem('shadigram_rental_selections');
                                const rentalSelections = rentalSaved ? JSON.parse(rentalSaved) : {};
                                const selection = rentalSelections[vendor._id] || rentalSelections[s._id];
                                return {
                                    id: s._id, // Internal shortlist ID
                                    realId: vendor._id,
                                    name: vendor.name,
                                    price: vendor.vendorType === 'rental' 
                                        ? (vendor.rentalDetails?.pricePerDay || 5000)
                                        : (vendor.vendorType === 'product' ? 50000 : 0), 
                                    image: vendor.images?.[0] || 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80',
                                    vendorName: vendor.name,
                                    vendorType: vendor.vendorType || 'service',
                                    category: vendor.category,
                                    status: s.status,
                                    rentalDetails: vendor.rentalDetails,
                                    rentalSelection: selection
                                };
                            });
                        allItems = [...backendMapped];
                    }
                } catch (err) {
                    console.error('Backend fetch failed', err);
                }
            }

            // 2. Fetch missing items from LocalStorage (for Guests or unsynced items)
            const existingVendorIds = new Set(allItems.map(item => (item as any).realId || item.id));
            
            for (const id of localIds) {
                const vendorId = id.startsWith('prod_') ? id.split('_')[1] : id;
                
                // Only fetch if not already in allItems
                if (!existingVendorIds.has(vendorId) && !existingVendorIds.has(id)) {
                    try {
                        const vRes = await fetch(`${API_BASE_URL}/api/market-vendors/${vendorId}`);
                        if (vRes.ok) {
                            const vData = await vRes.json();
                            const vendor = vData.vendor;
                            const rentalSaved = localStorage.getItem('shadigram_rental_selections');
                            const rentalSelections = rentalSaved ? JSON.parse(rentalSaved) : {};
                            const selection = rentalSelections[vendor._id] || rentalSelections[id];
                            allItems.push({
                                id,
                                realId: vendor._id,
                                name: id.startsWith('prod_') ? `Premium Choice from ${vendor.name}` : vendor.name,
                                price: vendor.vendorType === 'rental'
                                    ? (vendor.rentalDetails?.pricePerDay || 5000)
                                    : (vendor.vendorType === 'product' ? 50000 : 0),
                                image: vendor.images?.[0] || 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80',
                                vendorName: vendor.name,
                                vendorType: vendor.vendorType || 'service',
                                category: vendor.category,
                                rentalDetails: vendor.rentalDetails,
                                rentalSelection: selection
                            });
                        }
                    } catch (err) {}
                }
            }

            setItems(allItems);
            
            // Initialize confirmation state for services
            const initialConfirmations: Record<string, boolean> = {};
            allItems.forEach(item => {
                if (item.vendorType === 'service') {
                    initialConfirmations[item.id] = item.status === 'hired';
                }
            });
            setConfirmedBookings(initialConfirmations);

        } catch (err) {
            setError('Failed to fetch your selection. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accessToken) {
            // First sync local to backend
            const localSaved = localStorage.getItem('shadigram_shortlist');
            if (localSaved) {
                const localIds = JSON.parse(localSaved);
                const vendorIds = localIds.map((id: string) => id.startsWith('prod_') ? id.split('_')[1] : id);
                
                fetch(`${API_BASE_URL}/api/market-vendors/shortlist/sync`, {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ vendorIds })
                })
                .then(() => {
                    localStorage.removeItem('shadigram_shortlist');
                    fetchCartItems();
                })
                .catch(() => fetchCartItems()); // Fallback to fetch what's on backend
            } else {
                fetchCartItems();
            }
        } else {
            // For Guests
            fetchCartItems();
        }
    }, [accessToken]);

    const handleRemove = async (id: string) => {
        try {
            if (accessToken) {
                const res = await fetch(`${API_BASE_URL}/api/market-vendors/shortlist/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                if (res.ok) {
                    setItems(prev => prev.filter(item => item.id !== id));
                    // Update local storage too
                    const localSaved = localStorage.getItem('shadigram_shortlist');
                    if (localSaved) {
                        const localIds = JSON.parse(localSaved);
                        const updatedIds = localIds.filter((lid: string) => lid !== id);
                        localStorage.setItem('shadigram_shortlist', JSON.stringify(updatedIds));
                    }
                }
            } else {
                // For Guests, only remove from local state and localStorage
                setItems(prev => prev.filter(item => item.id !== id));
                const localSaved = localStorage.getItem('shadigram_shortlist');
                if (localSaved) {
                    const localIds = JSON.parse(localSaved);
                    const updatedIds = localIds.filter((lid: string) => lid !== id);
                    localStorage.setItem('shadigram_shortlist', JSON.stringify(updatedIds));
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleConvertToPlan = async () => {
        if (!accessToken) {
            setError('Please login to integrate these with your Wedding Plan.');
            setTimeout(() => history.push('/login'), 2000);
            return;
        }
        setConverting(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/shortlist/convert`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            });
            const data = await res.json();
            if (res.ok) {
                setSuccess('Successfully integrated with your Wedding Plan! Check your Tasks.');
                setItems([]);
                localStorage.removeItem('shadigram_shortlist');
                setTimeout(() => history.push('/welcome'), 2000);
            } else {
                setError(data.message || 'Failed to integrate with planning');
            }
        } catch (err) {
            setError('Error connecting to server');
        } finally {
            setConverting(false);
        }
    };

    const handleBulkEnquiry = async (data: any) => {
        if (!accessToken) {
            history.push('/login');
            return;
        }

        const vendorIds = selectedVendors.length > 0 
            ? selectedVendors.map(v => v.id)
            : serviceItems.map(item => (item as any).realId || item.id);

        const res = await fetch(`${API_BASE_URL}/api/market-vendors/enquiry/bulk`, {
            method: 'POST',
            headers: { 
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ ...data, vendorIds })
        });
        
        if (!res.ok) throw new Error('Failed to send enquiry');
    };

    const getRentalItemBreakdown = (item: CartItem) => {
        const dailyRate = item.rentalDetails?.pricePerDay || item.price || 0;
        const deposit = item.rentalDetails?.securityDeposit || 0;
        
        let days = 1;
        let startDateStr = '';
        let endDateStr = '';
        let size = 'M';
        
        if (item.rentalSelection) {
            const { startDate, endDate, size: selSize } = item.rentalSelection;
            startDateStr = startDate || '';
            endDateStr = endDate || '';
            size = selSize || 'M';
            
            if (startDate && endDate) {
                const s = new Date(startDate);
                const e = new Date(endDate);
                const diff = e.getTime() - s.getTime();
                days = Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
            }
        }
        
        const rentalCost = dailyRate * days;
        const total = rentalCost + deposit;
        
        return {
            dailyRate,
            deposit,
            days,
            startDateStr,
            endDateStr,
            size,
            rentalCost,
            total
        };
    };

    const productItems = items.filter(item => item.vendorType === 'product');
    const serviceItems = items.filter(item => item.vendorType === 'service');
    const rentalItems = items.filter(item => item.vendorType === 'rental');

    const productSubtotal = productItems.reduce((sum, item) => sum + item.price, 0);
    const rentalCostSubtotal = rentalItems.reduce((sum, item) => {
        const breakdown = getRentalItemBreakdown(item);
        return sum + breakdown.rentalCost;
    }, 0);
    const securityDepositTotal = rentalItems.reduce((sum, item) => {
        const breakdown = getRentalItemBreakdown(item);
        return sum + breakdown.deposit;
    }, 0);

    const totalPrice = productSubtotal + rentalCostSubtotal + securityDepositTotal;
    const allBookingsConfirmed = serviceItems.length > 0 && serviceItems.every(item => confirmedBookings[item.id]);

    const handleCheckout = async () => {
        if (!accessToken) {
            setError('Please login to proceed with payment and bookings.');
            setTimeout(() => history.push('/login'), 2000);
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            if (rentalItems.length > 0) {
                for (const item of rentalItems) {
                    const breakdown = getRentalItemBreakdown(item);
                    
                    if (!breakdown.startDateStr || !breakdown.endDateStr) {
                        throw new Error(`Rental dates are missing for ${item.name}`);
                    }

                    const bookingPayload = {
                        marketVendorId: item.realId || item.id,
                        startDate: breakdown.startDateStr,
                        endDate: breakdown.endDateStr,
                        size: item.rentalDetails?.rentalCategory === 'dress' ? breakdown.size : undefined
                    };

                    const response = await fetch(`${API_BASE_URL}/api/rentals`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(bookingPayload)
                    });

                    const data = await response.json();
                    if (!response.ok) {
                        throw new Error(data.message || `Failed to rent ${item.name}`);
                    }

                    // Remove selection from localStorage
                    const rentalSaved = localStorage.getItem('shadigram_rental_selections');
                    if (rentalSaved) {
                        const rentalSelections = JSON.parse(rentalSaved);
                        delete rentalSelections[item.realId || item.id];
                        delete rentalSelections[item.id];
                        localStorage.setItem('shadigram_rental_selections', JSON.stringify(rentalSelections));
                    }

                    // Remove from shortlist
                    await fetch(`${API_BASE_URL}/api/market-vendors/shortlist/${item.id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    });
                }
            }

            setSuccess('Payment successful! Your bookings and purchases have been confirmed.');
            setItems([]);
            localStorage.removeItem('shadigram_shortlist');
            
            await fetchCartItems();
            setTimeout(() => history.push('/welcome'), 3000);

        } catch (err: any) {
            setError(err.message || 'Payment/Checkout failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="cart-page-loading">Loading your premium selection...</div>;

    return (
        <div className="cart-page">
            <header className="cart-page-header">
                <div className="container-premium">
                    <button className="back-link" onClick={() => history.push('/')}>
                        <ArrowLeft size={18} /> Continue Shopping
                    </button>
                    <h1>My Shopping & Bookings</h1>
                    <p>Manage your luxury wedding essentials and service inquiries</p>
                </div>
            </header>

            <main className="cart-page-main container-premium">
                <div className="cart-grid">
                    <div className="cart-items-column">
                        {/* Shopping Bag Section */}
                        <section className="cart-card">
                            <div className="card-header">
                                <h2><ShoppingBag /> Shopping Bag</h2>
                                <span className="item-count">{productItems.length} Products</span>
                            </div>
                            
                            {productItems.length === 0 ? (
                                <div className="empty-section-msg">
                                    <Package size={40} strokeWidth={1} />
                                    <p>Your shopping bag is empty</p>
                                </div>
                            ) : (
                                <div className="cart-item-list">
                                    {productItems.map(item => (
                                        <div key={item.id} className="cart-page-item">
                                            <img src={item.image} alt={item.name} />
                                            <div className="item-info">
                                                <h3>{item.name}</h3>
                                                <p className="vendor-name">by {item.vendorName}</p>
                                                <div className="item-meta">
                                                    <span className="category-tag">{item.category}</span>
                                                    <span className="price">₹{item.price.toLocaleString('en-IN')}</span>
                                                </div>
                                            </div>
                                            <button className="item-remove" onClick={() => handleRemove(item.id)}>
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Dress & Jewelry Rentals Section */}
                        <section className="cart-card">
                            <div className="card-header">
                                <h2><Sparkles className="text-gold" /> Dress & Jewelry Rentals</h2>
                                <span className="item-count">{rentalItems.length} Items</span>
                            </div>
                            
                            {rentalItems.length === 0 ? (
                                <div className="empty-section-msg">
                                    <Sparkles size={40} strokeWidth={1} />
                                    <p>No rental items reserved</p>
                                </div>
                            ) : (
                                <div className="cart-item-list">
                                    {rentalItems.map(item => {
                                        const breakdown = getRentalItemBreakdown(item);
                                        return (
                                            <div key={item.id} className="cart-page-item rental-style">
                                                <img src={item.image} alt={item.name} />
                                                <div className="item-info">
                                                    <h3>{item.name}</h3>
                                                    <p className="vendor-name">by {item.vendorName}</p>
                                                    
                                                    <div className="rental-selection-details">
                                                        {breakdown.startDateStr && breakdown.endDateStr && (
                                                            <div className="rental-detail-row">
                                                                <Calendar size={14} />
                                                                <span>
                                                                    {new Date(breakdown.startDateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(breakdown.endDateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} ({breakdown.days} {breakdown.days === 1 ? 'day' : 'days'})
                                                                </span>
                                                            </div>
                                                        )}
                                                        {item.rentalDetails?.rentalCategory === 'dress' && (
                                                            <div className="rental-detail-row">
                                                                <span className="size-label">Size:</span> <strong>{breakdown.size}</strong>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="item-meta">
                                                        <span className="category-tag">{item.category}</span>
                                                        <div className="price-breakdown">
                                                            <div className="rental-detail-row">
                                                                <span>Rent: </span>
                                                                <span className="price-value">₹{breakdown.rentalCost.toLocaleString('en-IN')}</span>
                                                                <span className="rate-text"> (₹{breakdown.dailyRate.toLocaleString('en-IN')}/day)</span>
                                                            </div>
                                                            <div className="rental-detail-row">
                                                                <span>Deposit: </span>
                                                                <span className="deposit-value">₹{breakdown.deposit.toLocaleString('en-IN')}</span>
                                                                <span className="deposit-text"> (Refundable)</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button className="item-remove" onClick={() => handleRemove(item.id)}>
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>

                        {/* Bookings Section */}
                        <section className="cart-card">
                            <div className="card-header">
                                <h2><Calendar /> My Bookings</h2>
                                <span className="item-count">{serviceItems.length} Services</span>
                            </div>
                            
                            {serviceItems.length === 0 ? (
                                <div className="empty-section-msg">
                                    <Clock size={40} strokeWidth={1} />
                                    <p>No service bookings shortlisted</p>
                                </div>
                            ) : (
                                <div className="cart-item-list">
                                    {serviceItems.map(item => (
                                        <div key={item.id} className="cart-page-item service-style">
                                            <div className="item-details-side">
                                                <img src={item.image} alt={item.name} />
                                                <div className="item-text">
                                                    <div className="category-tag-mini">{item.category}</div>
                                                    <h3>{item.name}</h3>
                                                    <p className="vendor-name">{item.vendorName}</p>
                                                </div>
                                            </div>
                                            
                                            <div className="item-actions-side">
                                                <div 
                                                    className={`booking-status-badge ${confirmedBookings[item.id] ? 'confirmed' : 'unconfirmed'}`}
                                                    onClick={() => handleToggleConfirmation(item.id)}
                                                    title={confirmedBookings[item.id] ? "Click to unconfirm" : "Click to confirm"}
                                                >
                                                    {confirmedBookings[item.id] ? (
                                                        <><CheckCircle size={14} /> <span>Confirmed</span></>
                                                    ) : (
                                                        <><Clock size={14} /> <span>Mark Confirmed</span></>
                                                    )}
                                                </div>
                                                
                                                <button 
                                                    className="pending-inquiry-btn"
                                                    onClick={() => handleSingleEnquiry((item as any).realId || item.id, item.vendorName)}
                                                >
                                                    <Clock size={12} /> Inquiry
                                                </button>
                                                
                                                <button className="item-action-remove" onClick={() => handleRemove(item.id)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    </div>

                    <div className="cart-summary-column sticky-sidebar">
                        {/* Shopping Summary */}
                        <div className="summary-card">
                            <h3>🛍️ Shopping Summary</h3>
                            
                            <div className="summary-rows">
                                <div className="summary-row">
                                    <span>Products Subtotal ({productItems.length})</span>
                                    <span>₹{productSubtotal.toLocaleString('en-IN')}</span>
                                </div>
                                {rentalItems.length > 0 && (
                                    <>
                                        <div className="summary-row">
                                            <span>Rentals Subtotal ({rentalItems.length})</span>
                                            <span>₹{rentalCostSubtotal.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="summary-row">
                                            <span>Refundable Deposits</span>
                                            <span>₹{securityDepositTotal.toLocaleString('en-IN')}</span>
                                        </div>
                                    </>
                                )}
                                <div className="summary-row">
                                    <span>Shipping</span>
                                    <span className="text-free">FREE</span>
                                </div>
                                <div className="total-divider"></div>
                                <div className="summary-row total">
                                    <span>Estimated Total</span>
                                    <span>₹{totalPrice.toLocaleString('en-IN')}</span>
                                </div>
                            </div>

                            <div className="cart-actions">
                                <button 
                                    className="btn-checkout-main" 
                                    disabled={productItems.length === 0 && rentalItems.length === 0}
                                    onClick={handleCheckout}
                                >
                                    Proceed to Pay <ChevronRight size={20} />
                                </button>
                                
                                <button 
                                    className="btn-try-home" 
                                    disabled={productItems.length === 0}
                                    onClick={() => setSuccess('Your Try-At-Home request has been sent!')}
                                >
                                    Try At Home <Package size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Bookings Summary */}
                        <div className="summary-card" style={{ marginTop: '2rem' }}>
                            <h3>📅 Booking Summary</h3>
                            
                            <div className="summary-rows">
                                <div className="summary-row">
                                    <span>Service Shortlisted</span>
                                    <span>{serviceItems.length} Vendors</span>
                                </div>
                                <div className="summary-row">
                                    <span>Confirmed Bookings</span>
                                    <span className={allBookingsConfirmed ? 'text-success' : 'text-warning'}>
                                        {Object.values(confirmedBookings).filter(Boolean).length} / {serviceItems.length}
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span>Consultation Fee</span>
                                    <span className="text-free">Complimentary</span>
                                </div>
                            </div>

                            <div className="cart-actions">
                                <button 
                                    className="btn-book-online" 
                                    disabled={serviceItems.length === 0 || converting}
                                    onClick={handleConvertToPlan}
                                >
                                    {converting ? 'Processing...' : (
                                        <>Add to Wedding Plan <CheckCircle size={20} /></>
                                    )}
                                </button>
                                
                                <button 
                                    className="btn-check-availability" 
                                    disabled={serviceItems.length === 0}
                                    onClick={() => {
                                        setSelectedVendors([]); // Clear single selection for bulk
                                        setIsAvailModalOpen(true);
                                    }}
                                >
                                    Bulk Availability Check <Clock size={18} />
                                </button>
                            </div>
                            <p className="action-hint" style={{ marginTop: '1.5rem' }}>
                                "Add to Wedding Plan" will integrate these services directly into your Shadigram Planning Dashboard as pending or confirmed bookings.
                            </p>
                        </div>

                        {error && <div className="error-alert">{error}</div>}
                        {success && <div className="success-alert">{success}</div>}
                    </div>
                </div>
            </main>
            <CheckAvailabilityModal 
                isOpen={isAvailModalOpen}
                onClose={() => setIsAvailModalOpen(false)}
                vendorNames={selectedVendors.length > 0 ? selectedVendors.map(v => v.name) : serviceItems.map(v => v.vendorName)}
                vendorIds={selectedVendors.length > 0 ? selectedVendors.map(v => v.id) : serviceItems.map(v => v.id)}
                onSubmit={handleBulkEnquiry}
            />
        </div>

    );
};

export default MyCart;
